// app/analytics/LateGA.tsx  - client component
"use client";
import { useEffect } from "react";
import { GA_ID, event } from "@/lib/gtag";
import {
  trackJourneyEvent,
  type JourneyEventParams,
} from "@/lib/journey-analytics";
import { hasConsent, CONSENT_CHANGED_EVENT } from "@/lib/legal/consent";

const TRACKED_SELECTOR = "[data-ga-event]";
const IMPRESSION_SELECTOR = '[data-ga-event][data-ga-impression="true"]';
const SCHOLARSHIP_MATCHER_PATH = "/blog/scholarships/matcher";
const ARTICLE_BODY_SELECTOR = ".post-content";

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: (...args: unknown[]) => void,
    options?: { timeout: number }
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
};

const STRING_DATASET_PARAM_MAPPINGS = [
  ["gaCategory", "category"],
  ["gaLabel", "label"],
  ["gaTool", "tool"],
  ["gaFilter", "filter"],
  ["gaValue", "value"],
  ["gaValue2", "value2"],
  ["gaAction", "action"],
  ["gaSource", "source"],
  ["gaJourney", "journey_name"],
  ["gaSourceCluster", "source_cluster"],
  ["gaSourcePage", "source_page"],
  ["gaSourceSurface", "source_surface"],
  ["gaSourceStage", "source_stage"],
  ["gaTargetStage", "target_stage"],
  ["gaTargetType", "target_type"],
  ["gaTargetId", "target_id"],
  ["gaCtaFamily", "cta_family"],
  ["gaCommercialIntent", "commercial_intent"],
  ["gaElementId", "element_id"],
  ["gaDestinationPath", "destination_path"],
  ["gaSearchInputMode", "search_input_mode"],
  ["gaSearchUiVariant", "search_ui_variant"],
  ["gaMeasurementContract", "measurement_contract"],
  ["gaStudySubject", "study_subject"],
  ["gaStudyTopic", "study_topic"],
  ["gaResourceType", "resource_type"],
  ["gaMatchQuality", "match_quality"],
] as const;

function assignDatasetParam(
  params: Record<string, unknown>,
  dataset: DOMStringMap,
  datasetKey: (typeof STRING_DATASET_PARAM_MAPPINGS)[number][0],
  paramKey: (typeof STRING_DATASET_PARAM_MAPPINGS)[number][1]
) {
  const value = dataset[datasetKey];
  if (typeof value === "string" && value.trim().length > 0) {
    params[paramKey] = value;
  }
}

function normalizeBooleanDatasetValue(
  value: string | undefined
): number | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "1" || normalized === "true" || normalized === "yes") {
    return 1;
  }
  if (normalized === "0" || normalized === "false" || normalized === "no") {
    return 0;
  }
  return undefined;
}

function buildDatasetParams(trackedElement: HTMLElement) {
  const params: Record<string, unknown> = {};
  for (const [datasetKey, paramKey] of STRING_DATASET_PARAM_MAPPINGS) {
    assignDatasetParam(params, trackedElement.dataset, datasetKey, paramKey);
  }
  if (trackedElement.dataset.gaResultRank) {
    const resultRank = Number.parseInt(trackedElement.dataset.gaResultRank, 10);
    if (Number.isFinite(resultRank)) params.result_rank = resultRank;
  }
  if (trackedElement.dataset.gaQueryLength) {
    const queryLength = Number.parseInt(
      trackedElement.dataset.gaQueryLength,
      10
    );
    if (Number.isFinite(queryLength)) params.query_length = queryLength;
  }
  if (trackedElement.dataset.gaGuidedChipCount) {
    const guidedChipCount = Number.parseInt(
      trackedElement.dataset.gaGuidedChipCount,
      10
    );
    if (Number.isFinite(guidedChipCount)) {
      params.guided_chip_count = guidedChipCount;
    }
  }
  if (trackedElement.dataset.gaAvailableResourceCount) {
    const availableResourceCount = Number.parseInt(
      trackedElement.dataset.gaAvailableResourceCount,
      10
    );
    if (Number.isFinite(availableResourceCount)) {
      params.available_resource_count = availableResourceCount;
    }
  }
  if (trackedElement.dataset.gaSearchBucket) {
    params.search_bucket = trackedElement.dataset.gaSearchBucket;
  }
  if (trackedElement.dataset.gaResultOrigin) {
    params.result_origin = trackedElement.dataset.gaResultOrigin;
  }
  const primaryFlag = normalizeBooleanDatasetValue(
    trackedElement.dataset.gaPrimary
  );
  if (primaryFlag !== undefined) params.is_primary_cta = primaryFlag;
  if (
    trackedElement instanceof HTMLAnchorElement &&
    !params.target_url &&
    trackedElement.href
  ) {
    params.target_url = trackedElement.href;
  }
  return params;
}

function trackUnlabelledMatcherLink(target: Element): boolean {
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor) return false;

  let targetPath: string;
  try {
    targetPath = new URL(anchor.href, window.location.origin).pathname;
  } catch {
    return false;
  }
  if (targetPath !== SCHOLARSHIP_MATCHER_PATH) return false;

  const sourcePath = window.location.pathname;
  const sourceCluster = sourcePath.startsWith("/blog/bursaries")
    ? "bursaries"
    : "scholarships";
  trackJourneyEvent("matcher_click", {
    commercial_intent: "assist",
    cta_family: "internal_route",
    journey_name: `${sourceCluster}_content_to_matcher`,
    source_cluster: sourceCluster,
    source_page: sourcePath.replace(/^\//, ""),
    source_stage: "decision",
    source_surface: "inline_link",
    target_id: "scholarship_matcher",
    target_stage: "utility",
    target_type: "tool",
    target_url: SCHOLARSHIP_MATCHER_PATH,
  });
  return true;
}

function classifyInternalLinkTarget(pathname: string): string {
  if (pathname.startsWith("/pdf/")) return "pdf";
  if (pathname.startsWith("/quizzes/")) return "quiz";
  if (pathname.startsWith("/videos/")) return "video";
  if (pathname.startsWith("/blog/")) return "article";
  return "internal_page";
}

function trackOrdinaryMdxInternalLink(target: Element): boolean {
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor || !anchor.closest(ARTICLE_BODY_SELECTOR)) return false;
  if (anchor.closest(TRACKED_SELECTOR)) return false;

  const rawHref = anchor.getAttribute("href")?.trim() ?? "";
  if (!rawHref || rawHref.startsWith("#")) return false;

  let linkUrl: URL;
  try {
    linkUrl = new URL(anchor.href, window.location.origin);
  } catch {
    return false;
  }

  const isInternal =
    linkUrl.origin === window.location.origin ||
    linkUrl.hostname === "eclat.local" ||
    linkUrl.hostname.endsWith("eclatinstitute.sg");
  if (!isInternal) return false;

  const targetPath = linkUrl.pathname;
  if (!targetPath || targetPath === window.location.pathname) return false;

  trackJourneyEvent("internal_link_click", {
    journey_name: "mdx_internal_navigation",
    label: anchor.textContent?.trim().replace(/\s+/g, " ").slice(0, 80) ?? "",
    measurement_contract: "mdx_internal_links_v1",
    source_page: window.location.pathname.replace(/^\//, ""),
    source_stage: "resource",
    source_surface: "mdx_body",
    target_id: targetPath.replace(/^\//, ""),
    target_stage: "resource",
    target_type: classifyInternalLinkTarget(targetPath),
    target_url: targetPath,
  });
  return true;
}

export default function LateGA() {
  useEffect(() => {
    if (!GA_ID) return;

    let cancelled = false;
    let analyticsLoaded = false;
    let trackedClickHandler: ((event: MouseEvent) => void) | null = null;
    let outboundClickHandler: ((event: MouseEvent) => void) | null = null;
    let impressionObserver: IntersectionObserver | null = null;
    let impressionMutationObserver: MutationObserver | null = null;
    let idleCallbackHandle: number | null = null;
    let idleTimeoutHandle: number | null = null;
    let firstInputHandler: (() => void) | null = null;

    const trackedClickOptions: AddEventListenerOptions = {
      capture: true,
      passive: true,
    };

    const detachTrackedClickListener = () => {
      if (!trackedClickHandler) return;
      document.removeEventListener(
        "click",
        trackedClickHandler,
        trackedClickOptions
      );
      trackedClickHandler = null;
    };

    const detachOutboundClickListener = () => {
      if (!outboundClickHandler) return;
      document.removeEventListener(
        "click",
        outboundClickHandler,
        trackedClickOptions
      );
      outboundClickHandler = null;
    };

    const detachImpressionObservers = () => {
      impressionObserver?.disconnect();
      impressionMutationObserver?.disconnect();
      impressionObserver = null;
      impressionMutationObserver = null;
    };

    const cleanupFirstInputListeners = () => {
      if (!firstInputHandler) return;
      removeEventListener("pointerdown", firstInputHandler);
      removeEventListener("keydown", firstInputHandler);
      removeEventListener("scroll", firstInputHandler);
      firstInputHandler = null;
    };

    const clearIdleBootstrapTrigger = () => {
      const idleWindow = window as IdleWindow;
      if (
        idleCallbackHandle != null &&
        typeof idleWindow.cancelIdleCallback === "function"
      ) {
        idleWindow.cancelIdleCallback(idleCallbackHandle);
      }
      if (idleTimeoutHandle != null) {
        window.clearTimeout(idleTimeoutHandle);
      }
      idleCallbackHandle = null;
      idleTimeoutHandle = null;
    };

    const attachTrackedClickListener = () => {
      if (trackedClickHandler) return;

      trackedClickHandler = (eventObject) => {
        if (cancelled) return;

        const target = eventObject.target;
        if (!(target instanceof Element)) return;

        const trackedElement = target.closest<HTMLElement>(TRACKED_SELECTOR);
        if (!trackedElement) {
          if (trackUnlabelledMatcherLink(target)) return;
          trackOrdinaryMdxInternalLink(target);
          return;
        }
        if (trackedElement.dataset.gaManual === "true") return;

        const action = trackedElement.dataset.gaEvent ?? "";
        if (!action) return;

        const params = buildDatasetParams(trackedElement);

        if (
          action === "tool_search_click" &&
          params.tool === "blog_search" &&
          params.result_origin == null
        ) {
          const fallbackResultOrigin =
            document.documentElement.dataset.blogSearchResultOrigin?.trim() ??
            "";
          if (fallbackResultOrigin) {
            params.result_origin = fallbackResultOrigin;
          }
        }

        if (typeof params.journey_name === "string") {
          trackJourneyEvent(action, params as JourneyEventParams);
          return;
        }

        event(action, params);
      };

      document.addEventListener(
        "click",
        trackedClickHandler,
        trackedClickOptions
      );
    };

    const attachImpressionObservers = () => {
      if (impressionObserver) return;
      if (typeof IntersectionObserver === "undefined") return;

      const seenElements = new WeakSet<Element>();
      impressionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || seenElements.has(entry.target)) {
              continue;
            }
            const trackedElement = entry.target;
            if (!(trackedElement instanceof HTMLElement)) continue;

            seenElements.add(trackedElement);
            impressionObserver?.unobserve(trackedElement);
            const params = buildDatasetParams(trackedElement);
            params.impression_of = trackedElement.dataset.gaEvent ?? "unknown";

            const impressionEvent =
              trackedElement.dataset.gaImpressionEvent?.trim() ||
              "cta_impression";
            if (typeof params.journey_name === "string") {
              trackJourneyEvent(impressionEvent, params as JourneyEventParams);
            } else {
              event(impressionEvent, params);
            }
          }
        },
        { threshold: 0.5 }
      );

      const observeWithin = (root: ParentNode) => {
        if (root instanceof Element && root.matches(IMPRESSION_SELECTOR)) {
          impressionObserver?.observe(root);
        }
        root
          .querySelectorAll<HTMLElement>(IMPRESSION_SELECTOR)
          .forEach((element) => impressionObserver?.observe(element));
      };

      observeWithin(document);
      impressionMutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) observeWithin(node);
          });
        }
      });
      impressionMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    };

    const attachOutboundClickListener = () => {
      if (outboundClickHandler) return;

      outboundClickHandler = (eventObject) => {
        if (cancelled) return;

        const target = eventObject.target;
        if (!(target instanceof Element)) return;

        const anchor = target.closest<HTMLAnchorElement>("a[href]");
        if (!anchor) return;

        const href = anchor.href;
        if (!href.startsWith("http")) return;

        // Skip links pointing to the current origin or first-party production
        // domains.
        try {
          const linkUrl = new URL(href);
          if (linkUrl.origin === window.location.origin) return;
          if (linkUrl.hostname.endsWith("eclatinstitute.sg")) return;
        } catch {
          return;
        }

        // Skip links already tracked by the data-ga-event handler.
        if (anchor.closest(TRACKED_SELECTOR)) return;

        event("outbound_click", {
          url: href,
          link_domain: new URL(href).hostname,
          link_text: anchor.textContent?.trim().slice(0, 50) ?? "",
        });
      };

      document.addEventListener(
        "click",
        outboundClickHandler,
        trackedClickOptions
      );
    };

    const ensureGtagStub = () => {
      if (typeof window === "undefined") return;

      const w = window as Window & {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
      };
      w.dataLayer = w.dataLayer || [];
      w.gtag =
        w.gtag ||
        function gtag() {
          // GA consumes the canonical gtag queue shape: dataLayer.push(arguments).
          // eslint-disable-next-line prefer-rest-params
          w.dataLayer?.push(arguments);
        };
    };

    const prepareAnalyticsCapture = () => {
      ensureGtagStub();
      attachTrackedClickListener();
      attachOutboundClickListener();
      attachImpressionObservers();
    };

    const loadAnalytics = async () => {
      if (cancelled || analyticsLoaded || !hasConsent("analytics")) return;
      analyticsLoaded = true;
      clearIdleBootstrapTrigger();
      cleanupFirstInputListeners();

      try {
        // Ensure delegated capture and the canonical gtag queue exist before
        // the async GA script resolves so early interactions are preserved.
        prepareAnalyticsCapture();

        const { default: injectGA4 } = await import(
          "@/app/analytics/injectGA4"
        );
        if (cancelled || !hasConsent("analytics")) {
          analyticsLoaded = false;
          return;
        }
        await injectGA4();
        if (!hasConsent("analytics")) analyticsLoaded = false;
        if (cancelled) return;
      } catch (error) {
        analyticsLoaded = false;
        console.error("Failed to load Google Analytics", error);
      }
    };

    const handleConsentChange = () => {
      if (hasConsent("analytics")) {
        prepareAnalyticsCapture();
        void loadAnalytics();
      } else {
        clearIdleBootstrapTrigger();
        cleanupFirstInputListeners();
        detachTrackedClickListener();
        detachOutboundClickListener();
        detachImpressionObservers();
      }
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    const cleanup = () => {
      cancelled = true;
      clearIdleBootstrapTrigger();
      cleanupFirstInputListeners();
      detachTrackedClickListener();
      detachOutboundClickListener();
      detachImpressionObservers();
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    };
    if (!hasConsent("analytics")) return cleanup;

    // Capture delegated clicks as soon as analytics consent is effective, but
    // keep the heavyweight GA script injection lazy.
    prepareAnalyticsCapture();

    firstInputHandler = () => {
      void loadAnalytics();
    };

    addEventListener("pointerdown", firstInputHandler, {
      once: true,
      passive: true,
    });
    addEventListener("keydown", firstInputHandler, { once: true });
    addEventListener("scroll", firstInputHandler, {
      once: true,
      passive: true,
    });

    const idleWindow = window as IdleWindow;
    if (typeof idleWindow.requestIdleCallback === "function") {
      idleCallbackHandle = idleWindow.requestIdleCallback(
        () => {
          void loadAnalytics();
        },
        { timeout: 2000 }
      );
    } else {
      idleTimeoutHandle = window.setTimeout(() => {
        void loadAnalytics();
      }, 2000);
    }

    return cleanup;
  }, []);

  return null;
}
